import { useDispatch } from "react-redux";
import { bTag, useModal } from "core";
import { CompetitionActionPanelActions } from "shared/reducers/CompetitionActionPanel";
import { useHistory } from "react-router-dom";
import { useProfile } from "@brg/auth";
import { CreateLinkTo } from "shared/constants/linkTo";
import { useCallback, useMemo, useState } from "react";
import { API, Competition, Endpoints, ProjectAPI, Tag } from "@brg/api";
import { useToasts } from "@brg/ui";
import urlApi from "shared/actions/urlApi";
import { useTranslation } from "react-i18next";
import useQueryState from "./useQueryState";
import { STATUSES } from "_competition/const";

const useCompetitionActions = (competition?: Competition) => {
    const dispatch = useDispatch();
    const modal = useModal();
    const history = useHistory();
    const profile = useProfile();
    const { error, info } = useToasts();
    const { t } = useTranslation();
    const [{ isPreview }, setQueryParams] = useQueryState();

    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

    const hasDocuments = useMemo(() => (!!competition?.nominations?.[0]?.documentsIds?.length), [competition])
    const canApplyWithoutProject = competition?.allowEmptyParticipantRequests;
    const needToFillForm = !!competition?.nominations?.[0]?.needToFillForm;
    const competitionId = competition?.competitionId;

    const isPreRegistration = useMemo(() => (
        !!competition?.isPreRegistration &&
        !competition?.participantAccountIds?.includes(profile?.accountId) &&
        competition?.status === STATUSES.Open
    ), [profile, competition])

    const tags: Tag[] = useMemo(
        () => [
            ...(competition?.regularTags || []),
            ...(competition?.requredTags || []),
            ...(competition?.tags || [])] || []
        , [competition])

    const tagString = useMemo(() => {
        return tags.map(tag => `#${bTag.title(tag)}`).join(' ')
    }, [tags])

    const isExam = competition?.lmsModuleProjectId;

    const isPaidApplication = competition?.isPaidApplication;

    const storeCompetition = useCallback(() => {
        if (!isExam) {
            dispatch(CompetitionActionPanelActions.setCurrentCompetition(competition));
        }
    }, [competition, isExam, dispatch]);

    const goToProfile = () => {
        storeCompetition();
        modal.hide()
        history.push(CreateLinkTo.designerNew(profile!));
    }

    const handleCreateProject = () => {
        storeCompetition();
        const requiredTagId = [...(competition?.requredTagIds || [])].pop();
        history.push(!!requiredTagId ? `${CreateLinkTo.projectCreate()}?tagId=${requiredTagId}` : CreateLinkTo.projectCreate());
        modal.hide();
    }

    const shouldDisplayNoProjectsCheckbox = useMemo(
        () => (!selectedProjects?.length && canApplyWithoutProject)
        , [selectedProjects, canApplyWithoutProject])

    const handleSubmitFor = useCallback(({
        projectName,
    }: { projectName: string }) => {
        modal.show('confirmation', {
            projectName,
            competitionId: competition?.competitionId,
            competitionPrice: competition?.competitionInfo?.applicationPrice,
            projectId: competition?.lmsModuleProjectId,
            selectedProjectId: selectedProjects[0],
            onConfirm: () => {
                console.log('Переход к оплате за проект:', projectName);
            },
            onCancel: () => {
                console.log('Отмена отправки');
            },
            onPaymentSuccess: () => {
                console.log('Оплата прошла успешно, проект отправлен на конкурс');
            },
            onPaymentFail: () => {
                modal.show('paymentErrorModal', {
                    title: 'Ошибка',
                    message: 'Оплата отклонена провайдером',
                    returnButtonText: 'Вернуться к модулю'
                });
            }
        });
    }, [selectedProjects, competition, modal]);


    const getLegalsModal = useCallback((flag: boolean, fn: Function) => {
        if (flag && competitionId && competition && competition.nominationsID && needToFillForm) {
            modal.show(isPreRegistration ? 'competition-custom-legals' : 'competition-legals', {
                projects: selectedProjects,
                nominationIds: [...competition.nominationsID],
                competitionId,
                hasDocuments,
                callback: () => {
                    fn(competitionId);
                },
            });
        } else fn(competitionId);
    }, [competition, modal, competitionId, selectedProjects, hasDocuments, needToFillForm, isPreRegistration])

    const onApplyClick = async ({
        forceSelectedProjectId,
        shouldPublish,
        projectId,
    }: { forceSelectedProjectId?: string, shouldPublish?: boolean, projectId?: string }) => {
        try {
            // Используем переданный selectedProjectId или текущий selectedProjects
            const projectsToUse = forceSelectedProjectId ? [forceSelectedProjectId] : selectedProjects;
            if (!forceSelectedProjectId) {
                const res = await new Promise((resolve, reject) => {
                    modal.show('sendConfirmation', {
                        onConfirm: () => {
                            (shouldPublish ? ProjectAPI.UpdateStatus({
                                projectStatus: 1,
                                projectId,
                            }) : Promise.resolve()).then((r) => {
                                if (shouldPublish) {
                                    if (r?.status !== 200) {
                                        throw new Error('Не удалось опубликовать проект');
                                    } else {
                                        setQueryParams({ isPreview: undefined });
                                        history.push(CreateLinkTo.project(projectId));
                                    }
                                }
                                resolve(true);
                            })
                        },
                        onCancel: () => {
                            modal.back();
                            resolve(false);
                        },
                    });
                })
                if (!res) return;
            }

            //@ts-ignore
            if (competition?.maxParticipantsCount && projectsToUse?.length > maxProjects) {
                error(`Вы не можете подать проекты на конкурс, так как модератор установил ограничение на количество. Обратитесь к модератору.`)
                return;
            }
            // проверка дедлайна приемки проектов
            const endDate = new Date(competition?.endDate || 0);
            const now = new Date();
            const isDeprecated = endDate < now
            if (isDeprecated) {
                modal.show('competition-sent-error', {
                    competitionEndDate: competition?.endDate,
                    onClick: () => {
                        window.location.reload()
                    },
                    onClose: () => window.location.reload(),
                });
                return;
            }

            //Потому что 2 запроса дублируются ф-ционал. ApplyNomination и Apply делают по сути одно и тоже
            //1-й проект отправляется с подтверждением подписания анкеты, а остальные без
            if ((projectsToUse?.length > 1 && needToFillForm) || !needToFillForm) {
                let res;
                const projects = needToFillForm ? [...projectsToUse]?.slice(1) : projectsToUse;
                if (isExam) {
                    res = await Promise.all(projects.map((value, idx) => {
                        return API.request()
                            .endpoint(Endpoints.competitions)
                            .url('/api/Competition/ApplyProjectToExam')
                            .post()
                            .addQuery('competitionId', competition?.competitionId || '')
                            .addQuery('ProjectId', value)
                            .send()
                    }))
                } else {
                    res = await Promise.all(projects.map((value, idx) => {
                        return urlApi().applyProjectToCompetition({
                            nominationId: competition?.nominationsID?.[0] || '',
                            projectId: value
                        });
                    }));
                }
                if (res?.some((r: any) => r.status !== 200)) {
                    throw new Error("Что-то пошло не так");
                }
            }

            if (!forceSelectedProjectId) {
                setSelectedProjects([]);
            }

            if (competitionId) {
                if (isExam) {
                    info('Проект успешно подан на экзамен')
                    return modal.show('competition-sent', {
                        competitionId,
                        isExam: isExam,
                        onClick: () => {
                            window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId)
                        },
                        onClose: () => window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId),
                    });
                }

                dispatch(CompetitionActionPanelActions.resetCurrentCompetition());
                modal.show('competitionProjectSent', ({
                    onClose: () => {
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    },
                    onClick: () => {
                        modal.hide();
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    }
                }));
            }

        } catch (e) {
            error(String(e));
        }
    };

    return {
        storeCompetition,
        goToProfile,
        handleCreateProject,
        selectedProjects,
        setSelectedProjects,
        isPaidApplication,
        shouldDisplayNoProjectsCheckbox,
        canApplyWithoutProject,
        needToFillForm,
        isPreRegistration,
        handleSubmitFor,
        onApplyClick,
        getLegalsModal,
        hasDocuments,
        isExam,
        tagString,
        tags,
    }
}

export default useCompetitionActions;import { useDispatch } from "react-redux";
import { bTag, useModal } from "core";
import { CompetitionActionPanelActions } from "shared/reducers/CompetitionActionPanel";
import { useHistory } from "react-router-dom";
import { useProfile } from "@brg/auth";
import { CreateLinkTo } from "shared/constants/linkTo";
import { useCallback, useMemo, useState } from "react";
import { API, Competition, Endpoints, ProjectAPI, Tag } from "@brg/api";
import { useToasts } from "@brg/ui";
import urlApi from "shared/actions/urlApi";
import { useTranslation } from "react-i18next";
import useQueryState from "./useQueryState";
import { STATUSES } from "_competition/const";

const useCompetitionActions = (competition?: Competition) => {
    const dispatch = useDispatch();
    const modal = useModal();
    const history = useHistory();
    const profile = useProfile();
    const { error, info } = useToasts();
    const { t } = useTranslation();
    const [{ isPreview }, setQueryParams] = useQueryState();

    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

    const hasDocuments = useMemo(() => (!!competition?.nominations?.[0]?.documentsIds?.length), [competition])
    const canApplyWithoutProject = competition?.allowEmptyParticipantRequests;
    const needToFillForm = !!competition?.nominations?.[0]?.needToFillForm;
    const competitionId = competition?.competitionId;

    const isPreRegistration = useMemo(() => (
        !!competition?.isPreRegistration &&
        !competition?.participantAccountIds?.includes(profile?.accountId) &&
        competition?.status === STATUSES.Open
    ), [profile, competition])

    const tags: Tag[] = useMemo(
        () => [
            ...(competition?.regularTags || []),
            ...(competition?.requredTags || []),
            ...(competition?.tags || [])] || []
        , [competition])

    const tagString = useMemo(() => {
        return tags.map(tag => `#${bTag.title(tag)}`).join(' ')
    }, [tags])

    const isExam = competition?.lmsModuleProjectId;

    const isPaidApplication = competition?.isPaidApplication;

    const storeCompetition = useCallback(() => {
        if (!isExam) {
            dispatch(CompetitionActionPanelActions.setCurrentCompetition(competition));
        }
    }, [competition, isExam, dispatch]);

    const goToProfile = () => {
        storeCompetition();
        modal.hide()
        history.push(CreateLinkTo.designerNew(profile!));
    }

    const handleCreateProject = () => {
        storeCompetition();
        const requiredTagId = [...(competition?.requredTagIds || [])].pop();
        history.push(!!requiredTagId ? `${CreateLinkTo.projectCreate()}?tagId=${requiredTagId}` : CreateLinkTo.projectCreate());
        modal.hide();
    }

    const shouldDisplayNoProjectsCheckbox = useMemo(
        () => (!selectedProjects?.length && canApplyWithoutProject)
        , [selectedProjects, canApplyWithoutProject])

    const handleSubmitFor = useCallback(({
        projectName,
    }: { projectName: string }) => {
        modal.show('confirmation', {
            projectName,
            competitionId: competition?.competitionId,
            competitionPrice: competition?.competitionInfo?.applicationPrice,
            projectId: competition?.lmsModuleProjectId,
            selectedProjectId: selectedProjects[0],
            onConfirm: () => {
                console.log('Переход к оплате за проект:', projectName);
            },
            onCancel: () => {
                console.log('Отмена отправки');
            },
            onPaymentSuccess: () => {
                console.log('Оплата прошла успешно, проект отправлен на конкурс');
            },
            onPaymentFail: () => {
                modal.show('paymentErrorModal', {
                    title: 'Ошибка',
                    message: 'Оплата отклонена провайдером',
                    returnButtonText: 'Вернуться к модулю'
                });
            }
        });
    }, [selectedProjects, competition, modal]);


    const getLegalsModal = useCallback((flag: boolean, fn: Function) => {
        if (flag && competitionId && competition && competition.nominationsID && needToFillForm) {
            modal.show(isPreRegistration ? 'competition-custom-legals' : 'competition-legals', {
                projects: selectedProjects,
                nominationIds: [...competition.nominationsID],
                competitionId,
                hasDocuments,
                callback: () => {
                    fn(competitionId);
                },
            });
        } else fn(competitionId);
    }, [competition, modal, competitionId, selectedProjects, hasDocuments, needToFillForm, isPreRegistration])

    const onApplyClick = async ({
        forceSelectedProjectId,
        shouldPublish,
        projectId,
    }: { forceSelectedProjectId?: string, shouldPublish?: boolean, projectId?: string }) => {
        try {
            // Используем переданный selectedProjectId или текущий selectedProjects
            const projectsToUse = forceSelectedProjectId ? [forceSelectedProjectId] : selectedProjects;
            if (!forceSelectedProjectId) {
                const res = await new Promise((resolve, reject) => {
                    modal.show('sendConfirmation', {
                        onConfirm: () => {
                            (shouldPublish ? ProjectAPI.UpdateStatus({
                                projectStatus: 1,
                                projectId,
                            }) : Promise.resolve()).then((r) => {
                                if (shouldPublish) {
                                    if (r?.status !== 200) {
                                        throw new Error('Не удалось опубликовать проект');
                                    } else {
                                        setQueryParams({ isPreview: undefined });
                                        history.push(CreateLinkTo.project(projectId));
                                    }
                                }
                                resolve(true);
                            })
                        },
                        onCancel: () => {
                            modal.back();
                            resolve(false);
                        },
                    });
                })
                if (!res) return;
            }

            //@ts-ignore
            if (competition?.maxParticipantsCount && projectsToUse?.length > maxProjects) {
                error(`Вы не можете подать проекты на конкурс, так как модератор установил ограничение на количество. Обратитесь к модератору.`)
                return;
            }
            // проверка дедлайна приемки проектов
            const endDate = new Date(competition?.endDate || 0);
            const now = new Date();
            const isDeprecated = endDate < now
            if (isDeprecated) {
                modal.show('competition-sent-error', {
                    competitionEndDate: competition?.endDate,
                    onClick: () => {
                        window.location.reload()
                    },
                    onClose: () => window.location.reload(),
                });
                return;
            }

            //Потому что 2 запроса дублируются ф-ционал. ApplyNomination и Apply делают по сути одно и тоже
            //1-й проект отправляется с подтверждением подписания анкеты, а остальные без
            if ((projectsToUse?.length > 1 && needToFillForm) || !needToFillForm) {
                let res;
                const projects = needToFillForm ? [...projectsToUse]?.slice(1) : projectsToUse;
                if (isExam) {
                    res = await Promise.all(projects.map((value, idx) => {
                        return API.request()
                            .endpoint(Endpoints.competitions)
                            .url('/api/Competition/ApplyProjectToExam')
                            .post()
                            .addQuery('competitionId', competition?.competitionId || '')
                            .addQuery('ProjectId', value)
                            .send()
                    }))
                } else {
                    res = await Promise.all(projects.map((value, idx) => {
                        return urlApi().applyProjectToCompetition({
                            nominationId: competition?.nominationsID?.[0] || '',
                            projectId: value
                        });
                    }));
                }
                if (res?.some((r: any) => r.status !== 200)) {
                    throw new Error("Что-то пошло не так");
                }
            }

            if (!forceSelectedProjectId) {
                setSelectedProjects([]);
            }

            if (competitionId) {
                if (isExam) {
                    info('Проект успешно подан на экзамен')
                    return modal.show('competition-sent', {
                        competitionId,
                        isExam: isExam,
                        onClick: () => {
                            window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId)
                        },
                        onClose: () => window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId),
                    });
                }

                dispatch(CompetitionActionPanelActions.resetCurrentCompetition());
                modal.show('competitionProjectSent', ({
                    onClose: () => {
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    },
                    onClick: () => {
                        modal.hide();
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    }
                }));
            }

        } catch (e) {
            error(String(e));
        }
    };

    return {
        storeCompetition,
        goToProfile,
        handleCreateProject,
        selectedProjects,
        setSelectedProjects,
        isPaidApplication,
        shouldDisplayNoProjectsCheckbox,
        canApplyWithoutProject,
        needToFillForm,
        isPreRegistration,
        handleSubmitFor,
        onApplyClick,
        getLegalsModal,
        hasDocuments,
        isExam,
        tagString,
        tags,
    }
}

export default useCompetitionActions;